import React from "react";
import {
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { typographyScale } from "../../../../../styles/theme";
import { money } from "./format";
import { LINE_STATUS } from "./cashTimeline";

// The same tokens DashboardTable paints with (styles/theme.js).
const BORDER = "1px solid var(--pv-table-border, rgba(170, 180, 198, 0.6))";
const HEADER_BG = "var(--pv-table-header-bg, hsla(250, 86%, 97%, 1))";
const HEADER_TEXT = "var(--pv-table-header-text, inherit)";

const Dash = () => (
  <Typography component="span" variant="inherit" color="text.disabled">
    —
  </Typography>
);

const monoSx = { fontFamily: (theme) => theme.typography.caption.fontFamily };
const numberSx = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const PAYOUT_LABEL = { manhour: "Payroll", material: "Vendor", subcontract: "Subcontractor" };

const STATUS_COLOR = {
  removed: "default",
  noTask: "default",
  planned: "default",
  inProgress: "info",
  awaitingApproval: "warning",
  billed: "secondary",
  collected: "success",
  settled: "primary",
  funding: "error",
};

const HEAD = [
  { label: "In scope" },
  { label: "Switch" },
  { label: "WBS Code" },
  { label: "Task Description" },
  { label: "Payout via" },
  { label: "Client value", isNumber: true },
  { label: "Cost", isNumber: true },
  { label: "Collected", isNumber: true },
  { label: "Paid out", isNumber: true },
  { label: "Work done", isNumber: true },
  { label: "Switch fires", isNumber: true },
  { label: "Client pays", isNumber: true },
  { label: "Vendor pays", isNumber: true },
  { label: "Cash held", isNumber: true },
  { label: "Status" },
];

function SwitchCell({ firedCount, packetCount }) {
  const isOn = firedCount === packetCount;
  const label = packetCount > 1 ? `${firedCount}/${packetCount}` : isOn ? "1" : "0";
  return (
    <Tooltip title={packetCount > 1 ? `${firedCount} of ${packetCount} packets switched` : isOn ? "Switched" : "Not switched"}>
      <Chip
        size="small"
        label={label}
        color={firedCount > 0 ? "secondary" : "default"}
        variant={isOn ? "filled" : "outlined"}
        sx={{ height: 20, minWidth: 40, fontVariantNumeric: "tabular-nums" }}
      />
    </Tooltip>
  );
}

/**
 * One row per line, read on the timeline's cursor day: how many of its
 * switches have fired, what's been collected and paid out for it, its dates
 * (work done -> switch fires after client approval -> client pays -> vendor
 * pays; those of its last packet) and where it stands. "Cash held" is always
 * >= 0 by construction: it's the vendor/payroll lag the policy sets.
 *
 * Clicking a row marks its events on the money chart; unticking "In scope"
 * takes the line out of every figure (a view-only what-if, not saved).
 *
 * @param schedule    computeSwitchSchedule() / buildDemoSwitchSchedule() result
 * @param lineStates  Map<code, lineStateOn() result> for the cursor day
 * @param flags       switchComplianceFlags() result - lines paid before any
 *                    switch fired
 */
export default function DashboardSwitchTable({
  schedule,
  lineStates,
  flags,
  excluded,
  onToggleLineInScope,
  selectedCode,
  onSelectLine,
}) {
  const flagged = new Set(flags.map((f) => f.code));

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 1180,
            "& .MuiTableCell-root": {
              borderRight: BORDER,
              borderBottom: BORDER,
              px: 1,
              py: 0.5,
              fontSize: typographyScale.fontSize.sm,
              whiteSpace: "nowrap",
            },
            "& .MuiTableCell-root:last-of-type": { borderRight: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              {HEAD.map((head) => (
                <TableCell
                  key={head.label}
                  align={head.isNumber ? "right" : "left"}
                  sx={{ bgcolor: HEADER_BG, color: HEADER_TEXT, fontSize: typographyScale.fontSize.xs, fontWeight: 700 }}
                >
                  {head.label}
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: HEADER_BG, width: 32 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedule.map((row) => {
              const isExcluded = excluded.has(row.code);
              const state = lineStates.get(row.code);
              const isSelected = selectedCode === row.code;
              const dimSx = isExcluded ? { opacity: 0.45 } : null;

              return (
                <TableRow
                  key={row.code}
                  hover
                  selected={isSelected}
                  onClick={() => onSelectLine(row.code)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={!isExcluded}
                      onChange={() => onToggleLineInScope(row.code)}
                      inputProps={{ "aria-label": `Keep ${row.desc} in scope` }}
                    />
                  </TableCell>
                  <TableCell sx={dimSx}>
                    <SwitchCell firedCount={state.firedCount} packetCount={row.packets.length} />
                  </TableCell>
                  <TableCell sx={{ ...monoSx, ...dimSx, color: "primary.main" }}>{row.displayCode}</TableCell>
                  <TableCell sx={dimSx}>{row.desc}</TableCell>
                  <TableCell sx={dimSx}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${PAYOUT_LABEL[row.payoutType] || row.payoutType}${row.hours ? ` · ${Math.round(row.hours).toLocaleString("en-US")} h` : ""}`}
                      sx={{ height: 18, fontSize: "0.625rem" }}
                    />
                    {row.resubmittals > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        · {row.resubmittals} resubmittal{row.resubmittals === 1 ? "" : "s"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...numberSx, ...dimSx }}>{money(row.value)}</TableCell>
                  <TableCell sx={{ ...numberSx, ...dimSx }}>{money(row.cost)}</TableCell>
                  <TableCell sx={{ ...numberSx, ...dimSx }}>{money(state.collected)}</TableCell>
                  <TableCell sx={{ ...numberSx, ...dimSx }}>{money(state.paid)}</TableCell>
                  <TableCell sx={{ ...monoSx, ...numberSx, ...dimSx }}>{row.workDoneDate || <Dash />}</TableCell>
                  <TableCell sx={{ ...monoSx, ...numberSx, ...dimSx }}>{row.switchDate || <Dash />}</TableCell>
                  <TableCell sx={{ ...monoSx, ...numberSx, ...dimSx }}>{row.clientPayDate || <Dash />}</TableCell>
                  <TableCell sx={{ ...monoSx, ...numberSx, ...dimSx }}>{row.vendorPayDate || <Dash />}</TableCell>
                  <TableCell
                    sx={{
                      ...monoSx,
                      ...numberSx,
                      ...dimSx,
                      color: row.cashHeldDays == null ? undefined : "success.main",
                    }}
                  >
                    {row.cashHeldDays == null ? <Dash /> : `+${row.cashHeldDays}d`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={STATUS_COLOR[state.status]}
                      label={LINE_STATUS[state.status]}
                      sx={{ height: 20 }}
                    />
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {flagged.has(row.code) && (
                      <Tooltip title="Paid before any of this line's switches fired - the payout ran ahead of the policy.">
                        <ErrorOutlineIcon fontSize="inherit" color="error" />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
